import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Copy,
  Trash2,
  ChevronDown,
  ChevronRight,
  Star,
  Play,
  Users,
} from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'

/* ── Types ── */

interface TemplateItem {
  id: string
  role: string
  group: string
  headcount: number
  notes: string | null
  sortOrder: number
}

interface Template {
  id: string
  artistId: string | null
  name: string
  isDefault: boolean
  roles?: TemplateItem[]
  _count?: { roles: number }
}

interface Tour {
  id: string
  name: string
}

/* ── Constants ── */

const SHOW_ROLE_NAMES = [
  'principal_artist',
  'musical_director',
  'backing_singer',
  'band_musician',
  'tour_manager',
  'production_manager',
  'personal_assistant',
  'artist_security',
  'foh_engineer',
  'monitor_engineer',
  'playback_engineer',
  'sound_tech',
  'lighting_director',
  'lighting_operator',
  'video_director',
  'spotlight_operator',
  'stage_manager',
  'backline_tech',
  'guitar_tech',
  'drum_tech',
  'wardrobe_supervisor',
  'hair_makeup',
  'catering_manager',
  'medic',
  'head_rigger',
  'rigger',
  'stagehand',
  'truck_driver',
  'catering_crew',
  'dressing_room_runner',
  'local_driver',
  'interpreter',
  'loadout_crew',
  'local_security',
] as const

const ROLE_TO_GROUP: Record<string, string> = {
  principal_artist: 'artist_and_music',
  musical_director: 'artist_and_music',
  backing_singer: 'artist_and_music',
  band_musician: 'artist_and_music',
  tour_manager: 'management_and_ops',
  production_manager: 'management_and_ops',
  personal_assistant: 'management_and_ops',
  artist_security: 'management_and_ops',
  foh_engineer: 'sound',
  monitor_engineer: 'sound',
  playback_engineer: 'sound',
  sound_tech: 'sound',
  lighting_director: 'lighting_and_visuals',
  lighting_operator: 'lighting_and_visuals',
  video_director: 'lighting_and_visuals',
  spotlight_operator: 'lighting_and_visuals',
  stage_manager: 'stage_and_backline',
  backline_tech: 'stage_and_backline',
  guitar_tech: 'stage_and_backline',
  drum_tech: 'stage_and_backline',
  wardrobe_supervisor: 'wardrobe_and_personal',
  hair_makeup: 'wardrobe_and_personal',
  catering_manager: 'wardrobe_and_personal',
  medic: 'wardrobe_and_personal',
}

const GROUP_LABELS: Record<string, string> = {
  artist_and_music: 'Artist & Music',
  management_and_ops: 'Management & Ops',
  sound: 'Sound',
  lighting_and_visuals: 'Lighting & Visuals',
  stage_and_backline: 'Stage & Backline',
  wardrobe_and_personal: 'Wardrobe & Personal',
  local_production: 'Local Production',
  venue_staff: 'Venue Staff',
}

function roleLabel(role: string): string {
  return role
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function getGroupForRole(role: string): string {
  return ROLE_TO_GROUP[role] ?? 'local_production'
}

/* ── Main page ── */

export function TourTemplatesPage() {
  const qc = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [applyingToTour, setApplyingToTour] = useState<string | null>(null)

  const { data: templates, isLoading } = useQuery<Template[], ApiClientError>({
    queryKey: ['tour-templates'],
    queryFn: () => artistFetch<Template[]>('/tour-templates'),
  })

  const { data: tours } = useQuery<Tour[], ApiClientError>({
    queryKey: ['tours'],
    queryFn: () => artistFetch<Tour[]>('/tours'),
  })

  function toggleExpand(id: string) {
    setExpandedId((prev) => (prev === id ? null : id))
  }

  async function deleteTemplate(id: string) {
    if (!confirm('Delete this template?')) return
    try {
      await artistFetch(`/tour-templates/${id}`, { method: 'DELETE' })
      if (expandedId === id) setExpandedId(null)
      qc.invalidateQueries({ queryKey: ['tour-templates'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error')
    }
  }

  async function cloneTemplate(id: string) {
    try {
      await artistFetch(`/tour-templates/${id}/clone`, { method: 'POST' })
      qc.invalidateQueries({ queryKey: ['tour-templates'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error')
    }
  }

  async function setDefault(id: string) {
    try {
      await artistFetch(`/tour-templates/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isDefault: true }),
      })
      qc.invalidateQueries({ queryKey: ['tour-templates'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error')
    }
  }

  async function applyToTour(templateId: string, tourId: string) {
    if (!confirm('This will replace all existing tour members. Continue?')) return
    try {
      await artistFetch(`/tour-templates/${templateId}/apply-to-tour/${tourId}`, {
        method: 'POST',
      })
      setApplyingToTour(null)
      qc.invalidateQueries({ queryKey: ['tours'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error')
    }
  }

  return (
    <div>
      <PageHeader
        title="Tour Templates"
        subtitle="Define standard roles for tours. Create from the system default, customise for your artist, then apply to new tours."
        actions={
          <button
            type="button"
            onClick={() => setCreating((c) => !c)}
            className="flex items-center gap-2 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
          >
            <Plus className="h-4 w-4" />
            {creating ? 'Cancel' : 'Create template'}
          </button>
        }
      />

      {creating && (
        <CreateTemplateForm
          templates={templates ?? []}
          onCreated={() => {
            setCreating(false)
            qc.invalidateQueries({ queryKey: ['tour-templates'] })
          }}
        />
      )}

      {isLoading && (
        <p className="text-sm text-ovation-muted">Loading templates...</p>
      )}

      <div className="mt-4 space-y-3">
        {templates?.map((t) => (
          <div
            key={t.id}
            className="rounded-lg border border-ovation-ink/10 bg-ovation-paper"
          >
            {/* Card header */}
            <div className="flex items-center gap-3 p-4">
              <button
                type="button"
                onClick={() => toggleExpand(t.id)}
                className="text-ovation-muted hover:text-ovation-ink"
              >
                {expandedId === t.id ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>

              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{t.name}</span>
                  {t.isDefault && (
                    <span className="flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800">
                      <Star className="h-3 w-3" /> Default
                    </span>
                  )}
                  {t.artistId === null && (
                    <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-blue-800">
                      System
                    </span>
                  )}
                </div>
                <div className="mt-0.5 flex items-center gap-1 text-xs text-ovation-muted">
                  <Users className="h-3 w-3" />
                  {t._count?.roles ?? 0} roles
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1">
                {t.artistId !== null && !t.isDefault && (
                  <button
                    type="button"
                    onClick={() => setDefault(t.id)}
                    title="Set as default"
                    className="rounded p-1.5 text-ovation-muted hover:bg-ovation-ink/5 hover:text-amber-600"
                  >
                    <Star className="h-4 w-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => cloneTemplate(t.id)}
                  title="Clone"
                  className="rounded p-1.5 text-ovation-muted hover:bg-ovation-ink/5 hover:text-ovation-ink"
                >
                  <Copy className="h-4 w-4" />
                </button>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() =>
                      setApplyingToTour(applyingToTour === t.id ? null : t.id)
                    }
                    title="Apply to tour"
                    className="rounded p-1.5 text-ovation-muted hover:bg-ovation-ink/5 hover:text-green-600"
                  >
                    <Play className="h-4 w-4" />
                  </button>
                  {applyingToTour === t.id && (
                    <div className="absolute right-0 top-full z-10 mt-1 w-56 rounded-md border border-ovation-ink/10 bg-white p-2 shadow-lg">
                      <p className="mb-1 text-xs font-medium text-ovation-muted">
                        Apply to tour:
                      </p>
                      {tours && tours.length > 0 ? (
                        tours.map((tour) => (
                          <button
                            key={tour.id}
                            type="button"
                            onClick={() => applyToTour(t.id, tour.id)}
                            className="block w-full rounded px-2 py-1 text-left text-sm hover:bg-ovation-ink/5"
                          >
                            {tour.name}
                          </button>
                        ))
                      ) : (
                        <p className="text-xs text-ovation-muted">No tours yet</p>
                      )}
                    </div>
                  )}
                </div>
                {t.artistId !== null && (
                  <button
                    type="button"
                    onClick={() => deleteTemplate(t.id)}
                    title="Delete"
                    className="rounded p-1.5 text-ovation-muted hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Expanded items */}
            {expandedId === t.id && <TemplateDetail templateId={t.id} />}
          </div>
        ))}
      </div>
    </div>
  )
}

/* ── Template detail (expanded) ── */

function TemplateDetail({ templateId }: { templateId: string }) {
  const qc = useQueryClient()
  const [addingItem, setAddingItem] = useState(false)

  const { data: template, isLoading } = useQuery<Template, ApiClientError>({
    queryKey: ['tour-templates', templateId],
    queryFn: () => artistFetch<Template>(`/tour-templates/${templateId}`),
  })

  async function removeItem(itemId: string) {
    try {
      await artistFetch(`/tour-templates/${templateId}/items/${itemId}`, {
        method: 'DELETE',
      })
      qc.invalidateQueries({ queryKey: ['tour-templates'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error')
    }
  }

  if (isLoading) {
    return <p className="px-4 pb-4 text-sm text-ovation-muted">Loading...</p>
  }

  if (!template?.roles) return null

  // Group items by group
  const grouped = template.roles.reduce<Record<string, TemplateItem[]>>(
    (acc, item) => {
      const g = item.group
      if (!acc[g]) acc[g] = []
      acc[g].push(item)
      return acc
    },
    {},
  )

  const isSystem = template.artistId === null

  return (
    <div className="border-t border-ovation-ink/10 px-4 pb-4 pt-3">
      {Object.entries(grouped).map(([group, items]) => (
        <div key={group} className="mb-3">
          <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-ovation-muted">
            {GROUP_LABELS[group] ?? group}
          </h4>
          <div className="space-y-1">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-ovation-ink/5"
              >
                <span className="flex-1">{roleLabel(item.role)}</span>
                <span className="rounded bg-ovation-ink/10 px-1.5 py-0.5 text-[10px] font-medium">
                  x{item.headcount}
                </span>
                {item.notes && (
                  <span className="text-xs text-ovation-muted">{item.notes}</span>
                )}
                {!isSystem && (
                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    className="rounded p-1 text-ovation-muted hover:text-red-600"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {!isSystem && (
        <>
          {addingItem ? (
            <AddItemForm
              templateId={templateId}
              onAdded={() => {
                setAddingItem(false)
                qc.invalidateQueries({ queryKey: ['tour-templates'] })
              }}
              onCancel={() => setAddingItem(false)}
            />
          ) : (
            <button
              type="button"
              onClick={() => setAddingItem(true)}
              className="mt-2 flex items-center gap-1 text-xs text-ovation-muted hover:text-ovation-ink"
            >
              <Plus className="h-3 w-3" /> Add role
            </button>
          )}
        </>
      )}
    </div>
  )
}

/* ── Create template form ── */

function CreateTemplateForm({
  templates,
  onCreated,
}: {
  templates: Template[]
  onCreated: () => void
}) {
  const [name, setName] = useState('')
  const [cloneFrom, setCloneFrom] = useState('')
  const [error, setError] = useState('')

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    try {
      await artistFetch('/tour-templates', {
        method: 'POST',
        body: JSON.stringify({
          name,
          ...(cloneFrom && { cloneFrom }),
        }),
      })
      onCreated()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Error')
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mb-4 rounded-lg border border-ovation-ink/10 bg-ovation-paper p-4"
    >
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <label className="mb-1 block text-xs font-medium text-ovation-muted">
            Template name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Full Band Tour"
            className="w-full rounded border border-ovation-ink/20 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
            required
          />
        </div>
        <div className="w-60">
          <label className="mb-1 block text-xs font-medium text-ovation-muted">
            Clone from (optional)
          </label>
          <select
            value={cloneFrom}
            onChange={(e) => setCloneFrom(e.target.value)}
            className="w-full rounded border border-ovation-ink/20 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
          >
            <option value="">Start blank</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
        >
          Create
        </button>
      </div>
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </form>
  )
}

/* ── Add item form ── */

function AddItemForm({
  templateId,
  onAdded,
  onCancel,
}: {
  templateId: string
  onAdded: () => void
  onCancel: () => void
}) {
  const [role, setRole] = useState<string>(SHOW_ROLE_NAMES[0])
  const [headcount, setHeadcount] = useState(1)
  const [notes, setNotes] = useState('')
  const [error, setError] = useState('')

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    try {
      await artistFetch(`/tour-templates/${templateId}/items`, {
        method: 'POST',
        body: JSON.stringify({
          role,
          group: getGroupForRole(role),
          headcount,
          ...(notes && { notes }),
        }),
      })
      onAdded()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Error')
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-2 rounded border border-ovation-ink/10 bg-ovation-ink/5 p-3"
    >
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label className="mb-1 block text-[10px] font-medium text-ovation-muted">
            Role
          </label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full rounded border border-ovation-ink/20 px-2 py-1.5 text-sm focus:border-ovation-ink focus:outline-none"
          >
            {SHOW_ROLE_NAMES.map((r) => (
              <option key={r} value={r}>
                {roleLabel(r)} ({GROUP_LABELS[getGroupForRole(r)]})
              </option>
            ))}
          </select>
        </div>
        <div className="w-20">
          <label className="mb-1 block text-[10px] font-medium text-ovation-muted">
            Count
          </label>
          <input
            type="number"
            min={1}
            value={headcount}
            onChange={(e) => setHeadcount(Number(e.target.value))}
            className="w-full rounded border border-ovation-ink/20 px-2 py-1.5 text-sm focus:border-ovation-ink focus:outline-none"
          />
        </div>
        <div className="w-40">
          <label className="mb-1 block text-[10px] font-medium text-ovation-muted">
            Notes
          </label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional"
            className="w-full rounded border border-ovation-ink/20 px-2 py-1.5 text-sm focus:border-ovation-ink focus:outline-none"
          />
        </div>
        <button
          type="submit"
          className="rounded bg-ovation-ink px-3 py-1.5 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
        >
          Add
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded px-3 py-1.5 text-sm text-ovation-muted hover:text-ovation-ink"
        >
          Cancel
        </button>
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </form>
  )
}
